import { openDB, setData, getData, deleteData } from "@/lib/idb-core";

const DB_NAME = "KorimyImages";
const DB_VERSION = 1;

const TABLES_BY_VERSION: Record<number, string[]> = {
  1: ["chat-images", "note-images", "etc-images"],
};

const ALL_TABLES = Array.from(new Set(Object.values(TABLES_BY_VERSION).flat()));

// 캐시 크기 제한 (1GB)
const MAX_CACHE_SIZE = 1 * 1024 * 1024 * 1024;

interface CachedImage {
  url: string;
  blob: Blob;
  timestamp: number;
  chatId?: string;
}

interface CacheMetadata {
  totalSize: number;
  itemCount: number;
  lastUpdated: number;
}

const METADATA_KEY = "__cache_metadata__";

const initializeDB = (): Promise<IDBDatabase> => {
  return openDB(DB_NAME, DB_VERSION, (event: IDBVersionChangeEvent) => {
    const db = (event.target as IDBOpenDBRequest).result;
    for (let version = event.oldVersion + 1; version <= DB_VERSION; version++) {
      const tables = TABLES_BY_VERSION[version] || [];
      tables.forEach((table) => {
        if (!db.objectStoreNames.contains(table)) {
          db.createObjectStore(table, { keyPath: "id" });
        }
      });
    }
  });
};

// 메타데이터 조회
const getMetadata = async (): Promise<CacheMetadata> => {
  try {
    const db = await initializeDB();
    const result = await getData<CacheMetadata>(
      db,
      "chat-images",
      METADATA_KEY,
      ALL_TABLES,
    );

    if (result?.data) {
      return result.data;
    }

    // 메타데이터가 없으면 전체 스캔하여 생성
    return await rebuildMetadata();
  } catch (error) {
    console.error("Failed to get metadata:", error);
    return { totalSize: 0, itemCount: 0, lastUpdated: Date.now() };
  }
};

// 메타데이터 업데이트
const updateMetadata = async (
  sizeDelta: number,
  countDelta: number,
): Promise<void> => {
  try {
    const metadata = await getMetadata();

    const newMetadata: CacheMetadata = {
      totalSize: Math.max(0, metadata.totalSize + sizeDelta),
      itemCount: Math.max(0, metadata.itemCount + countDelta),
      lastUpdated: Date.now(),
    };

    const db = await initializeDB();
    await setData(db, "chat-images", METADATA_KEY, newMetadata, ALL_TABLES);
  } catch (error) {
    console.error("Failed to update metadata:", error);
  }
};

// 메타데이터 재구축 (전체 스캔)
const rebuildMetadata = async (): Promise<CacheMetadata> => {
  try {
    const db = await initializeDB();
    const transaction = db.transaction(["chat-images"], "readonly");
    const store = transaction.objectStore("chat-images");
    const request = store.openCursor();

    return new Promise((resolve, reject) => {
      let totalSize = 0;
      let itemCount = 0;

      request.onsuccess = async (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor) {
          // 1. 현재 커서가 가리키는 데이터 처리
          const item = cursor.value;
          // 메타데이터 키는 제외
          if (item.id !== METADATA_KEY) {
            totalSize += item.data?.blob?.size || 0;
            itemCount += 1;
          }

          // 2. cursor.continue() 호출
          cursor.continue(); // ← 이것이 다시 onsuccess를 트리거
        } else {
          // 3. 커서가 null이면 모든 항목 순회 완료
          const metadata: CacheMetadata = {
            totalSize,
            itemCount,
            lastUpdated: Date.now(),
          };

          // 메타데이터 저장
          await setData(db, "chat-images", METADATA_KEY, metadata, ALL_TABLES);
          resolve(metadata);
        }
      };

      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error("Failed to rebuild metadata:", error);
    return { totalSize: 0, itemCount: 0, lastUpdated: Date.now() };
  }
};

// 이미지를 Blob으로 다운로드하여 저장
export const cacheImage = async (
  url: string,
  chatId?: string,
): Promise<void> => {
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error("Failed to fetch image");

    const blob = await response.blob();

    // 빠른 용량 체크
    const metadata = await getMetadata();
    if (metadata.totalSize + blob.size > MAX_CACHE_SIZE) {
      await cleanupOldestCache();
    }

    const imageData: CachedImage = {
      url,
      blob,
      timestamp: Date.now(),
      chatId,
    };

    const db = await initializeDB();
    await setData(db, "chat-images", url, imageData, ALL_TABLES);

    // 메타데이터 업데이트
    await updateMetadata(blob.size, 1);
  } catch (error) {
    console.error("Failed to cache image:", error);
  }
};

// 캐시된 이미지 조회
export const getCachedImage = async (url: string): Promise<string | null> => {
  try {
    const db = await initializeDB();
    const result = await getData<CachedImage>(
      db,
      "chat-images",
      url,
      ALL_TABLES,
    );

    if (result?.data?.blob) {
      return URL.createObjectURL(result.data.blob);
    }
    return null;
  } catch (error) {
    console.error("Failed to get cached image:", error);
    return null;
  }
};

// 캐시된 이미지 삭제
export const deleteCachedImage = async (url: string): Promise<void> => {
  try {
    const db = await initializeDB();

    // 삭제 전 크기 확인
    const result = await getData<CachedImage>(
      db,
      "chat-images",
      url,
      ALL_TABLES,
    );

    const size = result?.data?.blob?.size || 0;

    await deleteData(db, "chat-images", url, ALL_TABLES);

    // 메타데이터 업데이트
    if (size > 0) {
      await updateMetadata(-size, -1);
    }
  } catch (error) {
    console.error("Failed to delete cached image:", error);
  }
};

// 빠른 캐시 크기 확인 (메타데이터 사용)
export const getCacheSize = async (): Promise<number> => {
  try {
    const metadata = await getMetadata();
    return metadata.totalSize;
  } catch (error) {
    console.error("Failed to get cache size:", error);
    return 0;
  }
};

// 캐시 항목 개수 확인
export const getCacheItemCount = async (): Promise<number> => {
  try {
    const metadata = await getMetadata();
    return metadata.itemCount;
  } catch (error) {
    console.error("Failed to get cache item count:", error);
    return 0;
  }
};

// 오래된 캐시 정리 (개선된 버전)
export const cleanupOldestCache = async (
  targetSize: number = MAX_CACHE_SIZE * 0.9,
): Promise<void> => {
  try {
    const db = await initializeDB();
    const transaction = db.transaction(["chat-images"], "readwrite");
    const store = transaction.objectStore("chat-images");
    const request = store.openCursor();

    const items: { url: string; timestamp: number; size: number }[] = [];

    request.onsuccess = async (event) => {
      const cursor = (event.target as IDBRequest).result;
      if (cursor) {
        const item = cursor.value;
        // 메타데이터는 제외
        if (item.id !== METADATA_KEY) {
          items.push({
            url: item.id,
            timestamp: item.data?.timestamp || 0,
            size: item.data?.blob?.size || 0,
          });
        }
        cursor.continue();
      } else {
        // 모든 항목 수집 완료, 타임스탬프 기준 오름차순 정렬
        items.sort((a, b) => a.timestamp - b.timestamp);

        let currentSize = items.reduce((sum, item) => sum + item.size, 0);
        let deletedSize = 0;
        let deletedCount = 0;

        for (const item of items) {
          if (currentSize <= targetSize) break;

          await deleteData(db, "chat-images", item.url, ALL_TABLES);
          currentSize -= item.size;
          deletedSize += item.size;
          deletedCount += 1;
        }

        // 메타데이터 업데이트
        if (deletedCount > 0) {
          await updateMetadata(-deletedSize, -deletedCount);
        }

        console.log(
          `[Cache] Cleaned up ${deletedCount} items (${(deletedSize / 1024 / 1024).toFixed(2)}MB)`,
        );
      }
    };
  } catch (error) {
    console.error("Failed to cleanup oldest cache:", error);
  }
};

// 전체 캐시 삭제
export const clearAllCache = async (): Promise<void> => {
  try {
    const db = await initializeDB();
    const transaction = db.transaction(["chat-images"], "readwrite");
    const store = transaction.objectStore("chat-images");

    await new Promise<void>((resolve, reject) => {
      const request = store.clear();
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });

    // 메타데이터 초기화
    const metadata: CacheMetadata = {
      totalSize: 0,
      itemCount: 0,
      lastUpdated: Date.now(),
    };
    await setData(db, "chat-images", METADATA_KEY, metadata, ALL_TABLES);

    console.log("[Cache] All cache cleared");
  } catch (error) {
    console.error("Failed to clear cache:", error);
  }
};

// 캐시 상태 조회 (디버깅용)
export const getCacheStats = async (): Promise<{
  size: number;
  count: number;
  maxSize: number;
  usagePercent: number;
}> => {
  const metadata = await getMetadata();
  return {
    size: metadata.totalSize,
    count: metadata.itemCount,
    maxSize: MAX_CACHE_SIZE,
    usagePercent: (metadata.totalSize / MAX_CACHE_SIZE) * 100,
  };
};
